import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFileText, FiCheckCircle, FiClock, FiLayers, FiPlusCircle, FiArrowRight } from 'react-icons/fi';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const font = "'Outfit', sans-serif";
const BL = '#6366f1';
const C = { text: '#1e1b4b', sub: '#6b7280', border: '#e0e7ff', bg: '#eef2ff', white: '#fff' };

const StatCard = ({ icon, label, value, color, gradient }) => (
    <div style={{ background: C.white, borderRadius: 16, padding: '16px', border: `1.5px solid ${C.border}`, boxShadow: '0 4px 16px rgba(99,102,241,0.07)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, flexShrink: 0 }}>{icon}</div>
        <div>
            <div style={{ fontSize: 24, fontWeight: 900, color, fontFamily: font, lineHeight: 1 }}>{value ?? 0}</div>
            <div style={{ fontSize: 11, color: C.sub, fontWeight: 700, fontFamily: font, marginTop: 4 }}>{label}</div>
        </div>
    </div>
);

const statusBadge = (status) => {
    const map = { draft: ['#f59e0b', '#fffbeb'], submitted: ['#6366f1', '#eef2ff'], approved: ['#10b981', '#ecfdf5'], rejected: ['#ef4444', '#fef2f2'] };
    const [color, bg] = map[status] || ['#6b7280', '#f9fafb'];
    return <span style={{ padding: '3px 10px', borderRadius: 20, background: bg, color, fontSize: 11, fontWeight: 700, fontFamily: font }}>{status}</span>;
};

const TeacherDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        api.get('/api/teacher/dashboard')
            .then(r => setData(r.data))
            .catch(() => setData({ stats: {}, recentPapers: [], myAssignments: [] }))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12, flexDirection: 'column' }}>
            <div style={{ width: 38, height: 38, border: '4px solid #e0e7ff', borderTopColor: BL, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ color: C.sub, fontFamily: font, fontSize: 13 }}>Loading dashboard…</p>
        </div>
    );

    const s = data?.stats || {};

    return (
        <div style={{ width: '100%', fontFamily: font }}>
            {/* Welcome banner */}
            <div style={{ borderRadius: 20, background: 'linear-gradient(135deg,#818cf8 0%,#6366f1 60%,#4f46e5 100%)', padding: '20px 24px', marginBottom: 20, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                <div>
                    <h1 style={{ fontSize: 20, fontWeight: 900, margin: '0 0 4px', letterSpacing: -0.5 }}>👋 Welcome, {user?.name?.split(' ')[0] || 'Teacher'}!</h1>
                    <p style={{ fontSize: 12, opacity: 0.9, margin: 0 }}>{user?.department || 'Department'} · {user?.designation || 'Faculty'}</p>
                </div>
                {s.totalAssignments > 0 && (
                    <button onClick={() => navigate('/teacher/create-paper')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: font, backdropFilter: 'blur(6px)' }}>
                        <FiPlusCircle size={15} /> Create Paper
                    </button>
                )}
            </div>

            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
                <StatCard icon={<FiLayers size={18} />} label="Assigned Batches" value={s.totalAssignments} color="#6366f1" gradient="linear-gradient(135deg,#818cf8,#6366f1)" />
                <StatCard icon={<FiFileText size={18} />} label="Total Papers" value={s.totalPapers} color="#8b5cf6" gradient="linear-gradient(135deg,#a78bfa,#8b5cf6)" />
                <StatCard icon={<FiClock size={18} />} label="Submitted" value={s.submitted} color="#f59e0b" gradient="linear-gradient(135deg,#fbbf24,#f59e0b)" />
                <StatCard icon={<FiCheckCircle size={18} />} label="Approved" value={s.approved} color="#10b981" gradient="linear-gradient(135deg,#34d399,#10b981)" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 18 }}>
                {/* Recent Papers */}
                <div style={{ background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 3px 12px rgba(99,102,241,0.07)' }}>
                    <div style={{ padding: '14px 18px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13, color: C.text }}>
                            <span style={{ width: 4, height: 16, borderRadius: 3, background: 'linear-gradient(180deg,#818cf8,#6366f1)', display: 'inline-block' }} />
                            Recent Papers
                        </div>
                        <button onClick={() => navigate('/teacher/papers')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: BL, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: font }}>
                            View all <FiArrowRight size={12} />
                        </button>
                    </div>
                    {(!data?.recentPapers?.length) ? (
                        <div style={{ padding: 32, textAlign: 'center', color: C.sub, fontSize: 13 }}>No papers yet. {s.totalAssignments > 0 && <button onClick={() => navigate('/teacher/create-paper')} style={{ color: BL, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: font }}>Create one →</button>}</div>
                    ) : data.recentPapers.map(p => (
                        <div key={p.id} onClick={() => navigate(`/teacher/papers/${p.id}`)} style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.title}</div>
                                <div style={{ fontSize: 11, color: C.sub }}>{p.subject_name} · {p.batch_name} · Sem {p.semester}</div>
                            </div>
                            {statusBadge(p.status)}
                        </div>
                    ))}
                </div>

                {/* Assignments */}
                <div style={{ background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 3px 12px rgba(99,102,241,0.07)' }}>
                    <div style={{ padding: '14px 18px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 4, height: 16, borderRadius: 3, background: 'linear-gradient(180deg,#818cf8,#6366f1)', display: 'inline-block' }} />
                        My Batch Assignments
                    </div>
                    {(!data?.myAssignments?.length) ? (
                        <div style={{ padding: 32, textAlign: 'center', color: C.sub, fontSize: 13 }}>No assignments from admin yet.</div>
                    ) : data.myAssignments.map((a, i) => (
                        <div key={i} style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{a.subject_name}</div>
                            <div style={{ fontSize: 11, color: C.sub }}>{a.batch_name} · Sem {a.semester} · {a.academic_year}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TeacherDashboard;
